/**
 * Work Tracking Service
 * API calls for work tracking system
 */

import axios from 'axios';
import { baseUrl } from '../../config';

const API_BASE = `${baseUrl}/work-status`;
const ACTIVITY_BASE = `${baseUrl}/work-activity`;

// Get auth token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken'); // Changed from 'token' to 'accessToken'
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// ================== Work Status APIs ==================

export const clockIn = async (staff_id) => {
  try {
    const response = await axios.post(
      `${API_BASE}/clock-in`,
      { staff_id },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const clockOut = async (staff_id) => {
  try {
    const response = await axios.post(
      `${API_BASE}/clock-out`,
      { staff_id },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getCurrentStatus = async (staff_id) => {
  try {
    const response = await axios.get(
      `${API_BASE}/current/${staff_id}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const startBreak = async (staff_id, break_type, notes = '') => {
  try {
    const response = await axios.post(
      `${API_BASE}/start-break`,
      { staff_id, break_type, notes },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const endBreak = async (staff_id) => {
  try {
    const response = await axios.post(
      `${API_BASE}/end-break`,
      { staff_id },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getBreakTypes = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}/break-types`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTodayBreaks = async (staff_id) => {
  try {
    const response = await axios.get(
      `${API_BASE}/today-breaks/${staff_id}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTeamDashboard = async (date = null) => {
  try {
    const url = date
      ? `${API_BASE}/team-dashboard?date=${date}`
      : `${API_BASE}/team-dashboard`;
    const response = await axios.get(url, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ================== Work Activity APIs ==================

export const getWorkCategories = async () => {
  try {
    const response = await axios.get(
      `${ACTIVITY_BASE}/categories`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getSubtypes = async (category_key) => {
  try {
    const response = await axios.get(
      `${ACTIVITY_BASE}/subtypes/${category_key}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const addManualActivity = async (activityData) => {
  try {
    const response = await axios.post(
      `${ACTIVITY_BASE}/add-manual`,
      activityData,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTodaySummary = async (staff_id) => {
  try {
    const response = await axios.get(
      `${ACTIVITY_BASE}/today-summary/${staff_id}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTimeline = async (staff_id, date = null, category = null) => {
  try {
    let url = `${ACTIVITY_BASE}/timeline/${staff_id}`;
    const params = [];
    if (date) params.push(`date=${date}`);
    if (category) params.push(`category=${encodeURIComponent(category)}`);
    if (params.length > 0) url += `?${params.join('&')}`;

    const response = await axios.get(url, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getActivitiesByDate = async (staff_id, date) => {
  try {
    const response = await axios.get(
      `${ACTIVITY_BASE}/by-date/${staff_id}?date=${date}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteActivity = async (activity_id, staff_id) => {
  try {
    const response = await axios.delete(
      `${ACTIVITY_BASE}/delete/${activity_id}`,
      {
        ...getAuthHeaders(),
        data: { staff_id }
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getActivityStats = async (staff_id, start_date, end_date) => {
  try {
    const response = await axios.get(
      `${ACTIVITY_BASE}/stats/${staff_id}?start_date=${start_date}&end_date=${end_date}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTeamActivityReport = async (date = null) => {
  try {
    const url = date
      ? `${ACTIVITY_BASE}/team-report?date=${date}`
      : `${ACTIVITY_BASE}/team-report`;
    const response = await axios.get(url, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
